import { Badge } from "@/components/Badge.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { EstimatedMonthlyCostRange } from "@/components/EstimatedMonthlyCostRange.tsx";
import { NavigateAfterDelete } from "@/components/NavigateAfterDelete.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { WTabs, type WTabItem } from "@/components/WTabs.tsx";
import { WModalButton, WToolButton } from "@/components/button/WToolButton.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableBillingAccount } from "@/components/table/DetailsTableBillingAccount.tsx";
import { DetailsTableIpAddress } from "@/components/table/DetailsTableIpAddress.tsx";
import { MonthlyCostRow } from "@/components/table/DetailsTableMonthlyCost.tsx";
import { DetailsTableName } from "@/components/table/DetailsTableName.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { useConfig } from "@/config.ts";
import { getResourceById } from "@/modules/api/resourceTypeException.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { MachineSizeInfo } from "@/modules/compute/MachineSizeInfo.tsx";
import { statusDescriptions } from "@/modules/compute/statusLabels.tsx";
import { LocationDetail } from "@/modules/location/LocationDetail.tsx";
import { VpcRow } from "@/modules/network/VpcRow.tsx";
import type { AssignedIpAddress } from "@/modules/network/ipAddress/assignable.ts";
import * as ipQuery from "@/modules/network/ipAddress/ipAddressApi.ts";
import { vpcQueryAtom, type VpcWithType } from "@/modules/network/vpc/vpcApi.ts";
import { convertServiceVmToMachine, type KubernetesPrice } from "@/modules/pricing/resourcePricing.ts";
import { AccessContent } from "@/modules/services/Access.tsx";
import { BackupsContent } from "@/modules/services/Backups.tsx";
import { ConnectionDetailsContent } from "@/modules/services/ConnectionDetails.tsx";
import { IpWhitelistContent } from "@/modules/services/IpWhitelist.tsx";
import { MetricsContent } from "@/modules/services/Metrics.tsx";
import { NetworkingContent } from "@/modules/services/Networking.tsx";
import { ServiceDeleteModal } from "@/modules/services/ServiceDeleteModal.tsx";
import { allAssignedServicesQueryAtom, type ManagedServiceWithAssigned } from "@/modules/services/joinAssignedQuery.ts";
import { InfrastructureContent } from "@/modules/services/kubernetes/Infrastructure.tsx";
import { KubernetesReadynessBadge } from "@/modules/services/kubernetes/KubernetesReadynessBadge.tsx";
import { KubernetesStatusBadge } from "@/modules/services/kubernetes/KubernetesStatusBadge.tsx";
import { MaintenanceContent } from "@/modules/services/kubernetes/Maintenance.tsx";
import { ServiceOsInfo } from "@/modules/services/os/ServiceOsInfo.tsx";
import { allServiceImagesByNameAtom } from "@/modules/services/serviceImagesQuery.ts";
import {
    deleteServiceMutation,
    hibernateServiceResourceMutation,
    startServiceMutation,
    stopServiceMutation,
    updateServiceMutation,
} from "@/modules/services/servicesQuery.ts";
import { useDeletableResource } from "@/utils/query/useDeletableResource.tsx";
import { useQueryResultAtom, useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type {
    ManagedKubernetesService,
    ManagedServicesVmBody,
    ServiceUpdateBody,
} from "@warrenio/api-spec/spec.oats.gen";
import { filterFalse } from "@warrenio/utils/collections/filterFalse";
import { mustGet } from "@warrenio/utils/collections/mustGet";
import type { ReactNode } from "react";
import { Link } from "react-aria-components";
import { entries, first, groupBy, mapValues } from "remeda";

export function ServiceView({ serviceId, location }: { serviceId: string; location: string }) {
    //#region Hooks
    const { privateNetworksEnabled } = useConfig();
    const { data: vpcs } = useQueryResultAtom(vpcQueryAtom(location));
    const allImages = useSuspenseQueryAtom(allServiceImagesByNameAtom);
    const services = useSuspenseQueryAtom(allAssignedServicesQueryAtom);

    const deleteMutation = useDeleteServiceMutation();
    const managedService = useDeletableResource(
        () => getResourceById(services, serviceId, "managed_service"),
        deleteMutation,
    );
    //#endregion

    if (managedService === undefined) {
        return <NavigateAfterDelete />;
    }

    const { resources, service } = managedService;
    const os = mustGet(allImages, service, "allImages");
    const { read_more_url } = os;
    const vms = resources.filter((item: ManagedServicesVmBody) => item.resource_type === "vm");
    const vm = first(vms);

    // TODO: Review this logic.
    const vpc =
        vpcs && vm ? [...vpcs.values()].find((item: VpcWithType) => item.vm_uuids.includes(vm.resource_id)) : undefined;
    const hasNetworkTab = privateNetworksEnabled && !!vpc;

    const dbTabs: WTabItem[] =
        service === "mariadb" || service === "postgresql"
            ? [
                  {
                      id: "connection",
                      title: <Trans>Connection details</Trans>,
                      content: <ConnectionDetailsContent item={managedService} />,
                  },
                  {
                      id: "allowlist",
                      title: (
                          <Trans comment="A list of IPs permitted to access the service | Legacy wording: IP whitelist">
                              IP allowlist
                          </Trans>
                      ),
                      content: <IpWhitelistContent item={managedService} />,
                  },
                  { id: "metrics", title: <Trans>Metrics</Trans>, content: <MetricsContent vms={vms} /> },
                  {
                      id: "backups",
                      title: <Trans>Backups</Trans>,
                      content: <BackupsContent service={managedService} />,
                  },
              ]
            : [];

    if (hasNetworkTab) {
        dbTabs.splice(3, 0, {
            id: "networking",
            title: <Trans>Networking</Trans>,
            content: <NetworkingContent service={managedService} vpc={vpc} />,
        });
    }

    const kbTabs: WTabItem[] =
        service === "kubernetes"
            ? [
                  {
                      id: "access",
                      title: <Trans>Access</Trans>,
                      content: <AccessContent item={managedService} />,
                  },
              ]
            : [];

    if (service === "kubernetes" && managedService.properties.spec) {
        const serviceSpec = managedService.properties.spec;

        if (serviceSpec.provider?.workers) {
            kbTabs.push({
                id: "infrastructure",
                title: <Trans>Infrastructure</Trans>,
                content: <InfrastructureContent item={managedService as ManagedKubernetesService} />,
            });
        }

        if (serviceSpec.maintenance) {
            kbTabs.push({
                id: "maintenance",
                title: <Trans>Maintenance</Trans>,
                content: <MaintenanceContent item={managedService as ManagedKubernetesService} />,
            });
        }
    }

    const tabs = filterFalse<WTabItem>([...dbTabs, ...kbTabs]);

    return (
        <>
            <ServiceToolbar
                item={managedService}
                deleteModal={<ServiceDeleteModal {...{ managedService, deleteMutation }} />}
            />
            <ServiceDetails service={managedService} vms={vms} vpc={vpc} />
            <Separator />
            <WTabs allTab autoSuspense items={tabs}>
                <ContentPane className="VStack gap-4">
                    <SectionHeader title={<Trans>Description</Trans>} />

                    <p>
                        <Trans>Fully managed database so you can focus on your code.</Trans>{" "}
                        {!!read_more_url && (
                            <Link href={read_more_url} target="_blank">
                                <Trans>Read more</Trans>
                            </Link>
                        )}
                    </p>
                </ContentPane>
            </WTabs>
        </>
    );
}

function ServiceToolbar({ item, deleteModal }: { item: ManagedServiceWithAssigned; deleteModal: ReactNode }) {
    const { t } = useLingui();
    const startMutation = useStandardMutation(startServiceMutation);
    const stopMutation = useStandardMutation(stopServiceMutation);
    const hibernateMutation = useStandardMutation(hibernateServiceResourceMutation);

    const { display_name, resources, service, properties } = item;

    const hasRunningVms = !!resources
        .filter((item) => item.resource_type === "vm")
        .find((item) => item.resource_allocation.status === "running");

    async function startService() {
        await startMutation.mutateAsync({ body: item });
    }

    async function stopService() {
        await stopMutation.mutateAsync({ body: item });
    }

    async function hibernateService(hibernation: boolean) {
        await hibernateMutation.mutateAsync({ uuid: item.uuid, body: { hibernation: hibernation } });
    }

    return (
        <Toolbar>
            {service === "kubernetes" ? (
                <>
                    <WToolButton
                        label={t`Awake`}
                        icon="jp-icon-run"
                        isDisabled={!properties?.spec?.hibernation.enabled}
                        action={() => hibernateService(false)}
                    />

                    <WModal
                        button={
                            <WModalButton
                                label={t`Hibernate`}
                                icon="jp-icon-stop"
                                isDisabled={properties?.spec?.hibernation.enabled}
                            />
                        }
                    >
                        <WModalContent
                            title={t`Hibernate`}
                            label={t`Hibernate`}
                            modalAction={() => hibernateService(true)}
                        >
                            <Trans>This will hibernate cluster "{display_name}".</Trans>{" "}
                            <Trans>
                                Pods and services hosted on this cluster will be stopped and any ephemeral storage will
                                be discarded.
                            </Trans>
                        </WModalContent>
                    </WModal>
                </>
            ) : (
                <>
                    <WToolButton label={t`Start`} icon="jp-icon-run" isDisabled={hasRunningVms} action={startService} />

                    <WModal button={<WModalButton label={t`Stop`} icon="jp-icon-stop" isDisabled={!hasRunningVms} />}>
                        <WModalContent title={t`Stop`} label={t`Stop`} modalAction={stopService}>
                            <Trans>
                                This will shut down your resource "{display_name}". If graceful shutdown does not finish
                                in 120 seconds, then a forceful stop will be carried out. For stopped resources you will
                                only be charged for the storage.
                            </Trans>
                        </WModalContent>
                    </WModal>
                </>
            )}

            {deleteModal}
        </Toolbar>
    );
}

function useDeleteServiceMutation() {
    return useStandardMutation(deleteServiceMutation);
}

export type DeleteServiceMutation = ReturnType<typeof useDeleteServiceMutation>;

function ServiceDetails({
    service,
    vms,
    vpc,
}: {
    service: ManagedServiceWithAssigned;
    vms: ManagedServicesVmBody[];
    vpc: VpcWithType | undefined;
}) {
    //#region Hooks
    const { t } = useLingui();
    const modifyMutation = useStandardMutation(updateServiceMutation);
    //#endregion

    const { display_name, $type, properties, uuid, created_at, billing_account_id, price } = service;
    const vm = first(vms);
    const machine = vm && convertServiceVmToMachine(vm, service);

    const byStatus = groupBy(vms, (item) => item.resource_allocation.status ?? "deleted");
    const statusCount = mapValues(byStatus, (items) => items.length);

    async function onModify({ display_name, billing_account_id }: ServiceUpdateBody) {
        await modifyMutation.mutateAsync({
            body: { ...(display_name && { display_name }), ...(billing_account_id && { billing_account_id }) },
            uuid,
        });
    }

    return (
        <ContentPane className="VStack gap-4">
            <ViewTitle title={display_name} />

            <DetailsHolder>
                <DetailsTable>
                    <DetailsTableName
                        value={display_name}
                        isRequired
                        onChange={async (name) => await onModify({ display_name: name })}
                    />

                    <DetailsTableRow title={t`Type`}>
                        <ResourceWithIcon type={$type} />
                    </DetailsTableRow>

                    <DetailsTableRow title={t`Service`}>
                        <div className="flex">
                            <ServiceOsInfo obj={service} />
                            {service.service !== "kubernetes" && (
                                <>
                                    {", "}
                                    {vms.length > 1 ? `${vms.length} nodes` : "single node"}
                                </>
                            )}
                        </div>
                    </DetailsTableRow>

                    <DetailsTableRow title={t`Status`}>
                        {service.service === "kubernetes" ? (
                            <KubernetesStatusBadge item={service} />
                        ) : (
                            entries(statusCount).map(([status, count]) => (
                                <Badge key={status} color={statusDescriptions[status].color}>
                                    {count} {count > 1 ? "VMs" : "VM"} {statusDescriptions[status].label}
                                </Badge>
                            ))
                        )}
                    </DetailsTableRow>

                    {service.service !== "kubernetes" && <ServicePublicIp item={service} />}

                    {"service_ip" in properties && (
                        <DetailsTableRow title={t`Private IPv4`}>
                            <ClipBoardTooltip isHtml>{properties.service_ip}</ClipBoardTooltip>
                        </DetailsTableRow>
                    )}

                    {service.service !== "kubernetes" && <VpcRow vpc={vpc} />}

                    {machine && (
                        <DetailsTableRow title={t`Size`}>
                            <MachineSizeInfo vm={machine} />
                        </DetailsTableRow>
                    )}

                    {service.service === "kubernetes" && (
                        <DetailsTableRow title={t`Readiness`}>
                            <KubernetesReadynessBadge item={service} />
                        </DetailsTableRow>
                    )}
                </DetailsTable>

                <DetailsTable>
                    <DetailsTableRow title={t`UUID`}>
                        <ClipBoardTooltip>{uuid}</ClipBoardTooltip>
                    </DetailsTableRow>

                    <LocationDetail slug={properties.location} />

                    <DetailsTableRow title={t`Created`}>
                        <LongDate date={created_at} />
                    </DetailsTableRow>

                    <DetailsTableRow />

                    <DetailsTableBillingAccount
                        valueKey={billing_account_id}
                        onChange={
                            service.service !== "kubernetes"
                                ? async (item) => await onModify({ billing_account_id: item.id })
                                : undefined
                        }
                    />

                    {service.service === "kubernetes" ? (
                        <KubernetesMonthlyCostRow item={service} billingAccountId={billing_account_id} />
                    ) : (
                        <MonthlyCostRow price={price} billingAccountId={billing_account_id} />
                    )}
                </DetailsTable>
            </DetailsHolder>
        </ContentPane>
    );
}

function ServicePublicIp({ item }: { item: ManagedServiceWithAssigned }) {
    const { location, assignedPublicIp, uuid } = item;

    const unAssignIpMutation = useStandardMutation(ipQuery.unassignIpAddressMutation);
    const assignIpAddressMutation = useStandardMutation(ipQuery.assignIpAddressMutation);

    async function assignIpAddress(address: AssignedIpAddress | null) {
        if (address) {
            await assignIpAddressMutation.mutateAsync({
                location,
                address: address.address,
                body: {
                    assigned_to_resource_type: "service",
                    assigned_to: uuid,
                },
            });
        } else if (assignedPublicIp) {
            await unAssignIpMutation.mutateAsync({ location, address: assignedPublicIp.address });
        }
    }

    return (
        <DetailsTableIpAddress
            value={assignedPublicIp}
            location={location}
            onChange={async (obj) => await assignIpAddress(obj)}
        />
    );
}

function KubernetesMonthlyCostRow({
    item,
    billingAccountId,
}: {
    item: ManagedServiceWithAssigned;
    billingAccountId?: number;
}) {
    const { t } = useLingui();

    const price = item.price as KubernetesPrice;

    return (
        <DetailsTableRow
            tooltip={t`Actual price is determined by compute and storage usage. Persistent volumes of hibernated clusters will still be charged for. See billing account current usage for more detailed information.`}
            title={t`Est. monthly cost`}
        >
            <b>
                <EstimatedMonthlyCostRange
                    minimum={price.minimum}
                    maximum={price.maximum}
                    billingAccountId={billingAccountId}
                />
            </b>
        </DetailsTableRow>
    );
}

// cspell:words Readyness
